import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getVineyards, getVineyardsCount, getRegions, getCountries } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { StarRating } from '../components/StarRating';
import { Pagination } from '../components/Pagination';
import { MapPin, Search, Plus, Filter, X } from 'lucide-react';

const DEFAULT_PAGE_SIZE = 12;

const Vineyards = () => {
  const { isAuthenticated, canAddContent, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [vineyards, setVineyards] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [loading, setLoading] = useState(true);
  const [appending, setAppending] = useState(false);
  const [search, setSearch] = useState('');
  const [activeSearch, setActiveSearch] = useState('');
  const [region, setRegion] = useState('');
  const [country, setCountry] = useState('');
  const [regions, setRegions] = useState([]);
  const [countries, setCountries] = useState([]);
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) {
      navigate('/login');
    }
  }, [authLoading, isAuthenticated, navigate]);

  useEffect(() => {
    (async () => {
      try {
        const [regionsData, countriesData] = await Promise.all([getRegions(), getCountries()]);
        setRegions(regionsData);
        setCountries(countriesData);
      } catch (e) {
        console.error('Failed to load filters:', e);
      }
    })();
  }, []);

  const buildParams = useCallback(() => {
    const params = {};
    if (activeSearch) params.search = activeSearch;
    if (region && region !== 'all') params.region = region;
    if (country && country !== 'all') params.country = country;
    return params;
  }, [activeSearch, region, country]);

  const loadPage = useCallback(async (targetPage, size, append = false, appendSkipOverride = null) => {
    if (append) setAppending(true);
    else setLoading(true);
    try {
      const params = {
        ...buildParams(),
        skip: appendSkipOverride != null ? appendSkipOverride : (targetPage - 1) * size,
        limit: size,
      };
      const [data, count] = await Promise.all([
        getVineyards(params),
        getVineyardsCount(buildParams()),
      ]);
      setVineyards((prev) => (append ? [...prev, ...data] : data));
      setTotalCount(count);
    } catch (e) {
      console.error('Failed to load vineyards:', e);
    } finally {
      setLoading(false);
      setAppending(false);
    }
  }, [buildParams]);

  useEffect(() => {
    loadPage(page, pageSize, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, activeSearch, region, country]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    setActiveSearch(search);
  };

  const clearFilters = () => {
    setSearch('');
    setActiveSearch('');
    setRegion('');
    setCountry('');
    setPage(1);
  };

  const handleLoadMore = () => {
    const skipOverride = (page - 1) * pageSize + vineyards.length;
    loadPage(page, pageSize, true, skipOverride);
  };

  const hasMore = (page - 1) * pageSize + vineyards.length < totalCount;

  return (
    <div className="min-h-screen pt-8 pb-20" data-testid="vineyards-page">
      <div className="container mx-auto px-4 md:px-8">
        {/* Header */}
        <div className="mb-12">
          <p className="text-xs uppercase tracking-[0.2em] text-[#d4af37] mb-2">Explore</p>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
            <h1 className="text-4xl md:text-5xl font-serif text-stone-50">Vineyards</h1>
            {isAuthenticated && canAddContent && (
              <Button
                onClick={() => navigate('/vineyards/new')}
                className="bg-[#d4af37] text-stone-950 hover:bg-[#b5952f] rounded-none uppercase tracking-widest text-xs px-6"
                data-testid="add-vineyard-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Vineyard
              </Button>
            )}
          </div>
        </div>

        {/* Search & Filters */}
        <div className="mb-8">
          <form onSubmit={handleSearch} className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-500" />
              <Input
                type="text"
                placeholder="Search vineyards, producers, regions..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-12 bg-stone-900/50 border-stone-700 rounded-none h-12"
                data-testid="search-input"
              />
            </div>
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowFilters(!showFilters)}
              className="border-stone-700 rounded-none h-12"
              data-testid="filter-toggle-btn"
            >
              <Filter className="w-4 h-4 mr-2" />
              Filters
            </Button>
            <Button
              type="submit"
              className="bg-[#d4af37] text-stone-950 hover:bg-[#b5952f] rounded-none h-12 px-8"
              data-testid="search-submit-btn"
            >
              Search
            </Button>
          </form>

          {showFilters && (
            <div className="mt-4 p-6 bg-stone-900/50 border border-stone-700" data-testid="filter-panel">
              <div className="flex flex-col md:flex-row gap-4">
                <div className="flex-1">
                  <label className="text-xs uppercase tracking-widest text-stone-500 mb-2 block">Region</label>
                  <Select value={region} onValueChange={(v) => { setRegion(v); setPage(1); }}>
                    <SelectTrigger className="bg-stone-900 border-stone-700 rounded-none" data-testid="region-select">
                      <SelectValue placeholder="All Regions" />
                    </SelectTrigger>
                    <SelectContent className="bg-stone-900 border-stone-700">
                      <SelectItem value="all">All Regions</SelectItem>
                      {regions.map((r) => (
                        <SelectItem key={r} value={r}>{r}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex-1">
                  <label className="text-xs uppercase tracking-widest text-stone-500 mb-2 block">Country</label>
                  <Select value={country} onValueChange={(v) => { setCountry(v); setPage(1); }}>
                    <SelectTrigger className="bg-stone-900 border-stone-700 rounded-none" data-testid="country-select">
                      <SelectValue placeholder="All Countries" />
                    </SelectTrigger>
                    <SelectContent className="bg-stone-900 border-stone-700">
                      <SelectItem value="all">All Countries</SelectItem>
                      {countries.map((c) => (
                        <SelectItem key={c} value={c}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-end">
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={clearFilters}
                    className="text-stone-500 hover:text-stone-300"
                    data-testid="clear-filters-btn"
                  >
                    <X className="w-4 h-4 mr-2" /> Clear
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={`sk-${i}`} className="bg-stone-900/40 border border-white/5 animate-pulse">
                <div className="aspect-[4/3] bg-stone-800" />
                <div className="p-6 space-y-3">
                  <div className="h-4 w-1/2 bg-stone-800 rounded" />
                  <div className="h-6 w-3/4 bg-stone-800 rounded" />
                  <div className="h-4 w-1/3 bg-stone-800 rounded" />
                </div>
              </div>
            ))}
          </div>
        ) : vineyards.length > 0 ? (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {vineyards.map((vineyard) => (
                <Link
                  key={vineyard.id}
                  to={`/vineyards/${vineyard.id}`}
                  className="vineyard-card group relative overflow-hidden bg-stone-900/40 border border-white/5"
                  data-testid={`vineyard-card-${vineyard.id}`}
                >
                  {vineyard.status && vineyard.status !== 'approved' && (
                    <span
                      className={`absolute top-3 left-3 z-10 px-2 py-1 text-[10px] uppercase tracking-widest border ${
                        vineyard.status === 'pending'
                          ? 'bg-[#d4af37]/20 border-[#d4af37]/50 text-[#d4af37]'
                          : 'bg-red-900/60 border-red-500/50 text-red-300'
                      }`}
                      data-testid={`vineyard-status-${vineyard.id}`}
                    >
                      {vineyard.status === 'pending' ? 'Pending' : 'Rejected'}
                    </span>
                  )}
                  <div className="aspect-[4/3] img-zoom">
                    <img
                      src={vineyard.photos?.[0] || 'https://images.unsplash.com/photo-1695700762236-3c61c5026858?w=600&q=80'}
                      alt={vineyard.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-stone-500 mb-2">
                      <MapPin className="w-3 h-3" />
                      {vineyard.region}, {vineyard.country}
                    </div>
                    <h3 className="text-xl font-serif text-stone-50 mb-2 group-hover:text-[#d4af37] transition-colors">
                      {vineyard.name}
                    </h3>
                    <p className="text-sm text-stone-500 mb-4 line-clamp-1">{vineyard.producer}</p>
                    <div className="flex items-center justify-between">
                      <StarRating rating={vineyard.average_rating || 0} readonly size="sm" />
                      <span className="text-xs text-stone-600">
                        {vineyard.rating_count} {vineyard.rating_count === 1 ? 'review' : 'reviews'}
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>

            <Pagination
              currentPage={page}
              totalItems={totalCount}
              pageSize={pageSize}
              loadedCount={vineyards.length}
              onPageChange={(p) => setPage(p)}
              onPageSizeChange={(s) => { setPageSize(s); setPage(1); }}
              onLoadMore={handleLoadMore}
              hasMore={hasMore}
              loading={appending}
            />
          </>
        ) : (
          <div className="text-center py-20">
            <MapPin className="w-16 h-16 text-stone-700 mx-auto mb-6" />
            <h3 className="text-2xl font-serif text-stone-400 mb-2">No Vineyards Found</h3>
            <p className="text-stone-600 mb-6">
              {activeSearch || region || country
                ? 'Try adjusting your search or filters'
                : 'Be the first to add a vineyard!'}
            </p>
            {isAuthenticated && canAddContent && (
              <Button
                onClick={() => navigate('/vineyards/new')}
                className="bg-[#d4af37] text-stone-950 hover:bg-[#b5952f] rounded-none"
                data-testid="empty-add-vineyard-btn"
              >
                Add First Vineyard
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Vineyards;
